import React, { useState } from 'react';
import Button from '../../../components/Button';
import { Project } from '../../../services/projects';
import { CompetitionAnalysis } from '../../../types/competitors';
import { enhanceDescription } from '../../../services/openai/openaiService';
import { getPlaceDetails, formatBusinessTypes, formatRating, formatOperatingHours } from '../../../services/google-places';
import CensusDataSection from './CensusDataSection';

interface OverviewTabProps {
  project: Project;
  competitionAnalysis: CompetitionAnalysis | null;
  editingDescription: boolean;
  descriptionValue: string;
  setEditingDescription: React.Dispatch<React.SetStateAction<boolean>>;
  setDescriptionValue: React.Dispatch<React.SetStateAction<string>>;
  handleSaveDescription: () => Promise<void>;
  handleRunAnalysis: () => Promise<void>;
  onViewReports: () => void;
}

// Simple Google Maps Component
interface GoogleMapProps {
  latitude: number;
  longitude: number;
  address: string;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ latitude, longitude, address }) => {
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDOMydSeKyRsZSRxpl-rd1PsFJOKGtxfew';
  
  // Create a static map URL for now (can be upgraded to interactive map later)
  const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?` +
    `center=${latitude},${longitude}&` +
    `zoom=15&` +
    `size=400x300&` +
    `markers=color:red%7C${latitude},${longitude}&` +
    `key=${GOOGLE_MAPS_API_KEY}`;

  const googleMapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`;

  return (
    <div style={{ marginTop: '1rem' }}>
      <h5 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#1e1e1e' }}>Map View</h5>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <img 
          src={mapUrl}
          alt={`Map showing ${address}`}
          style={{
            width: '100%',
            maxWidth: '400px',
            height: '300px',
            borderRadius: '4px',
            border: '1px solid #e0e0e0',
            cursor: 'pointer'
          }}
          onClick={() => window.open(googleMapsUrl, '_blank')}
        />
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '0.8rem',
          color: '#666'
        }}>
          Click to open in Google Maps
        </div>
      </div>
    </div>
  );
};

const OverviewTab: React.FC<OverviewTabProps> = ({
  project,
  competitionAnalysis,
  editingDescription,
  descriptionValue,
  setEditingDescription,
  setDescriptionValue,
  handleSaveDescription,
  handleRunAnalysis,
  onViewReports
}) => {
  // State for AI enhancement
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [originalDescription, setOriginalDescription] = useState('');
  
  // State for Google Places integration
  const [isLoadingGoogleInfo, setIsLoadingGoogleInfo] = useState(false);

  // Handle AI enhancement of description
  const handleEnhanceDescription = async () => {
    if (!descriptionValue) return;
    
    // Save original description for reset
    setOriginalDescription(descriptionValue);
    setIsEnhancing(true);
    
    try {
      const enhancedText = await enhanceDescription(descriptionValue);
      setDescriptionValue(enhancedText);
    } catch (error) {
      console.error('Error enhancing description:', error);
      alert('Failed to enhance description. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle Google Places info fetch using real service
  const handleGetInfoFromGoogle = async () => {
    // Check if project has a place_id
    if (!project.location_details?.place_id) {
      alert('No Google Place ID found for this project. Please ensure the project location was created with Google Places data.');
      return;
    }

    setIsLoadingGoogleInfo(true);
    
    try {
      // Use the real Google Places service
      const googlePlaceData = await getPlaceDetails(
        project.location_details.place_id,
        'name,formatted_phone_number,website,rating,user_ratings_total,opening_hours,types,business_status'
      );
      
      // Generate description from real Google Places data
      let generatedDescription = '';
      
      if (googlePlaceData.name) {
        generatedDescription += `${googlePlaceData.name}\n\n`;
      }
      
      if (googlePlaceData.types && googlePlaceData.types.length > 0) {
        const formattedTypes = formatBusinessTypes(googlePlaceData.types);
        generatedDescription += `Business Type: ${formattedTypes.join(', ')}\n\n`;
      }
      
      if (googlePlaceData.formatted_phone_number) {
        generatedDescription += `Phone: ${googlePlaceData.formatted_phone_number}\n`;
      }
      
      if (googlePlaceData.website) {
        generatedDescription += `Website: ${googlePlaceData.website}\n`;
      }
      
      if (googlePlaceData.rating) {
        const ratingText = formatRating(googlePlaceData.rating, googlePlaceData.user_ratings_total);
        generatedDescription += `Rating: ${ratingText}\n`;
      }

      if (googlePlaceData.business_status) {
        generatedDescription += `Status: ${googlePlaceData.business_status}\n`;
      }
      
      if (googlePlaceData.opening_hours) {
        const hoursText = formatOperatingHours(googlePlaceData.opening_hours);
        generatedDescription += `\nHours: ${hoursText}\n`;
        
        if (googlePlaceData.opening_hours.weekday_text && googlePlaceData.opening_hours.weekday_text.length > 0) {
          generatedDescription += `\nDetailed Hours:\n${googlePlaceData.opening_hours.weekday_text.join('\n')}\n`;
        }
      }
      
      // Save current description as original if not already set
      if (!originalDescription && descriptionValue) {
        setOriginalDescription(descriptionValue);
      }
      
      setDescriptionValue(generatedDescription.trim());
      
    } catch (error) {
      console.error('Error fetching Google Place info:', error);
      alert('Failed to fetch information from Google. Please try again.');
    } finally {
      setIsLoadingGoogleInfo(false);
    }
  };

  // Reset description to original
  const handleResetDescription = () => {
    if (originalDescription) {
      setDescriptionValue(originalDescription);
    } else if (project.description) {
      setDescriptionValue(project.description);
    }
  };

  // Format location display - Updated to use actual project structure
  const getLocationDisplay = () => {
    // First try location_details (which contains Google Places data)
    if (project.location_details?.formatted_address) {
      return project.location_details.formatted_address;
    }
    
    // Fallback to address field
    if (project.address) {
      return project.address;
    }
    
    // Fallback to legacy location field
    if (project.location) {
      if (typeof project.location === 'string') {
        return project.location;
      }
      
      if (project.location.address) {
        const parts = [
          project.location.address,
          project.location.city,
          project.location.state
        ].filter(Boolean);
        return parts.join(', ');
      }
    }
    
    return 'No location specified';
  };

  // Get coordinates for map and census data
  const getCoordinates = () => {
    // First try location_details
    if (project.location_details?.latitude && project.location_details?.longitude) {
      return {
        latitude: project.location_details.latitude,
        longitude: project.location_details.longitude
      };
    }
    
    // Fallback to coordinates field
    if (project.coordinates?.latitude && project.coordinates?.longitude) {
      return {
        latitude: project.coordinates.latitude,
        longitude: project.coordinates.longitude
      };
    }
    
    // Fallback to legacy location field
    if (project.location && typeof project.location === 'object' && project.location.coordinates) {
      return {
        latitude: project.location.coordinates.latitude,
        longitude: project.location.coordinates.longitude
      };
    }
    
    return null;
  };

  // Convert coordinates to census format
  const getCensusCoordinates = () => {
    const coords = getCoordinates();
    if (!coords) return undefined;
    
    return {
      lat: coords.latitude,
      lng: coords.longitude
    };
  };

  const coordinates = getCoordinates();
  const censusCoordinates = getCensusCoordinates();

  return (
    <div>
      <h3 style={{ fontSize: '1.4rem', marginBottom: '1.5rem', color: '#1e1e1e' }}>Project Overview</h3>
      
      {/* Enhanced Description Section */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ fontSize: '1.1rem', color: '#1e1e1e' }}>Description</h4>
          <Button 
            onClick={() => setEditingDescription(!editingDescription)}
            style={{ 
              padding: '0.5rem 1rem',
              fontSize: '0.9rem'
            }}
          >
            {editingDescription ? 'Cancel' : 'Edit'}
          </Button>
        </div>
        
        {editingDescription ? (
          <div>
            <textarea
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              style={{ 
                width: '100%', 
                minHeight: '150px', 
                padding: '0.75rem',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                fontSize: '1rem',
                lineHeight: '1.5',
                marginBottom: '1rem'
              }}
              placeholder="Enter project description"
            />
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <Button onClick={handleSaveDescription}>Save Description</Button>
              
              <Button 
                onClick={handleGetInfoFromGoogle}
                disabled={isLoadingGoogleInfo || !project.location_details?.place_id}
                style={{ 
                  backgroundColor: '#4285f4',
                  color: 'white'
                }}
              >
                {isLoadingGoogleInfo ? 'Loading...' : 'Get Info from Google'}
              </Button>
              
              <Button 
                onClick={handleEnhanceDescription}
                disabled={isEnhancing}
                style={{ 
                  backgroundColor: '#7FE7D9',
                  color: '#1e1e1e'
                }}
              >
                {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
              </Button>
              
              {(originalDescription || project.description) && (
                <Button 
                  onClick={handleResetDescription}
                  style={{ 
                    backgroundColor: 'white',
                    color: '#1e1e1e',
                    border: '1px solid #1e1e1e'
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ 
              minHeight: '100px',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <p style={{ color: '#333', margin: 0, whiteSpace: 'pre-line' }}>
                {project.description || 'No description provided.'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Button 
                onClick={() => {
                  setEditingDescription(true);
                  setDescriptionValue(project.description || '');
                }}
                style={{ 
                  backgroundColor: '#7FE7D9',
                  color: '#1e1e1e'
                }}
              >
                Edit Description
              </Button>
              
              <Button 
                onClick={() => {
                  setEditingDescription(true);
                  setDescriptionValue(project.description || '');
                  // Auto-trigger Google info fetch
                  setTimeout(() => handleGetInfoFromGoogle(), 100);
                }}
                disabled={isLoadingGoogleInfo || !project.location_details?.place_id}
                style={{ 
                  backgroundColor: '#4285f4',
                  color: 'white'
                }}
              >
                {isLoadingGoogleInfo ? 'Loading...' : 'Get Info from Google'}
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Location Summary (Read-only) with Map */}
      <div style={{ marginBottom: '2rem' }}>
        <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#1e1e1e' }}>Location</h4>
        <div style={{ 
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e0e0e0'
        }}>
          <p style={{ color: '#333', margin: '0 0 0.5rem 0', fontSize: '1rem' }}>
            {getLocationDisplay()}
          </p>
          
          {coordinates && (
            <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1rem 0' }}>
              Coordinates: {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
            </p>
          )}

          {project.location_details?.place_id && (
            <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1rem 0' }}>
              Google Place ID: {project.location_details.place_id}
            </p>
          )}
          
          {coordinates && (
            <GoogleMap 
              latitude={coordinates.latitude}
              longitude={coordinates.longitude}
              address={getLocationDisplay()}
            />
          )}
        </div>
      </div>
      
      {/* Census Data Section */}
      <CensusDataSection projectId={project.id} projectCoordinates={censusCoordinates} />
      
      {/* Actions Section */}
      <div>
        <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#1e1e1e' }}>Actions</h4>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Button onClick={handleRunAnalysis}>
            Run Analysis
          </Button>
          {competitionAnalysis && (
            <Button 
              onClick={onViewReports}
              style={{ 
                backgroundColor: 'white',
                color: '#1e1e1e',
                border: '1px solid #1e1e1e'
              }}
            >
              View Reports
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
